import { ENV } from '../../../../../config/runtimeEnv';
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { getSessionUser } from "../../../../../utils/auth/sessionUser";
import { Modal, Form, Input, Select, DatePicker, InputNumber, Divider, Row, Col, Tabs, Checkbox, Space, Typography, Badge, Progress, Alert, Descriptions, Button } from "antd";
import { SectionCard, ContrastTag } from "../../../../../components/shared";
import { InfoCircleOutlined, BookOutlined, CheckCircleOutlined, ClockCircleOutlined, WarningOutlined, StopOutlined, FileProtectOutlined, LinkOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { debounce } from "lodash";

import { setNewMedication, updateMedication } from "../../../../../api/healthcare/MedicationsApi";
import { fetchCatalogMedications } from "../../../../../api/healthcare/MedicationCatalogApi";
import { loadUserPreferences } from "../../../../../utils/userPrefs/UserPrefs";
import { Medication, SCRIPT_STATUS_CONFIG } from "../../../../../types/healthcare/MedicationTypes";
import { MedicationCatalogItem, DosageGuide, ADMINISTRATION_ROUTES, MEDICATION_CATEGORIES, FREQUENCY_OPTIONS, defaultDosageGuide } from "../../../../../types/healthcare/MedicationCatalogTypes";
import { useNotifications } from "../../../../../context/NotificationContext";
import { http } from "../../../../../lib/http/client";

const { TextArea } = Input;
const { Title, Text } = Typography;

const API_BASE_URL = ENV.VITE_HEALTHCARE_SERVER;

// Submit an e-script URL from the practitioner side
const submitEscriptPractitioner = async (medicationId: number, scriptUrl: string, notes?: string): Promise<{ data: Medication; message: string }> => {
	const res = await http.post(
		`${API_BASE_URL}/patients/medications/${medicationId}/submit-escript`,
		{ script_url: scriptUrl, notes }
	);
	return res.data;
};

// ── Status icon helpers ──────────────────────────────────────────────────────

const ScriptStatusIcon: React.FC<{ status?: Medication["script_status"] }> = ({ status }) => {
	switch (status) {
		case "pending":
			return <ClockCircleOutlined style={{ color: "#d46b08" }} />;
		case "uploaded":
			return <FileProtectOutlined style={{ color: "#0958d9" }} />;
		case "verified":
			return <CheckCircleOutlined style={{ color: "#389e0d" }} />;
		case "expired":
			return <StopOutlined style={{ color: "#cf1322" }} />;
		default:
			return <WarningOutlined style={{ color: "#8c8c8c" }} />;
	}
};

// ── Script tab label with action badge ──────────────────────────────────────

const ScriptTabLabel: React.FC<{ status?: Medication["script_status"] }> = ({ status }) => (
	<Space size={4}>
		<FileProtectOutlined />
		<span>Script</span>
		{status === "pending" && <Badge color="orange" />}
		{status === "uploaded" && <Badge color="blue" />}
		{status === "verified" && <Badge color="green" />}
		{status === "expired" && <Badge color="red" />}
	</Space>
);

// ── Props ────────────────────────────────────────────────────────────────────

interface CreateMedicationModalProps {
	currentMedication: Medication;
	setCurrentMedication: React.Dispatch<React.SetStateAction<Medication>>;
	isEditing: boolean;
	isVisible: boolean;
	onClose: () => void;
	isConsult: boolean;
	onSaved?: (medication: Medication) => void;
}

// ── Component ────────────────────────────────────────────────────────────────

const CreateMedicationModal: React.FC<CreateMedicationModalProps> = ({ currentMedication, setCurrentMedication, isEditing, onClose, isVisible, isConsult, onSaved }) => {
	const [activeTab, setActiveTab] = useState<string>("basic");
	const [medicationDetails, setMedicationDetails] = useState<any>({});
	const [loading, setLoading] = useState(false);
	const { showError, showSuccess } = useNotifications();
	const user = useMemo(() => getSessionUser(), []);
	const dateFormat = loadUserPreferences().dateFormat || "DD/MM/YYYY";

	// Catalog search
	const [catalogOptions, setCatalogOptions] = useState<{ value: number; label: string; item: MedicationCatalogItem }[]>([]);
	const [catalogSearching, setCatalogSearching] = useState(false);

	// Catalog dosage-guide picker — the selected medication + the guide currently applied
	const [selectedCatalogItem, setSelectedCatalogItem] = useState<MedicationCatalogItem | null>(null);
	const [selectedGuideLabel, setSelectedGuideLabel] = useState<string | undefined>(undefined);

	// E-script
	const [escriptSubmitting, setEscriptSubmitting] = useState(false);
	const [escriptUrl, setEscriptUrl] = useState("");

	// ── Init ──────────────────────────────────────────────────────────────────

	useEffect(() => {
		// Reset the catalog picker whenever the modal opens for a different medication
		setSelectedCatalogItem(null);
		setSelectedGuideLabel(undefined);
		if (!isEditing && !currentMedication.prescribed_by) {
			setCurrentMedication((prev) => ({ ...prev, prescribed_by: `${user.first_name} ${user.last_name}` }));
		}
		if (currentMedication.details) {
			try {
				const parsed = typeof currentMedication.details === "string" ? JSON.parse(currentMedication.details) : currentMedication.details;
				setMedicationDetails(parsed || {});
			} catch {
				setMedicationDetails({});
			}
		}
	}, [isEditing, currentMedication.id]);

	// ── Save ──────────────────────────────────────────────────────────────────

	const saveMedication = useCallback(async () => {
		if (!currentMedication) return;
		if (!currentMedication.medication_name || !currentMedication.dosage || !currentMedication.frequency || !currentMedication.start_date) {
			showError("Please fill in all required fields");
			return;
		}
		setLoading(true);
		try {
			const payload = { ...currentMedication, details: JSON.stringify(medicationDetails) };
			if (isEditing && currentMedication.id) {
				const result = await updateMedication(payload);
				showSuccess("Medication updated successfully");
				onSaved?.(result.data);
			} else {
				const result = await setNewMedication(payload);
				showSuccess("Medication added successfully");
				onSaved?.(result.data);
			}
			onClose();
		} catch (err) {
			console.error(err);
			showError("Failed to save medication");
		} finally {
			setLoading(false);
		}
	}, [currentMedication, medicationDetails, isEditing, onClose, showError, showSuccess]);

	// ── Helpers ───────────────────────────────────────────────────────────────

	const handleDetailChange = useCallback((field: string, value: any) => {
		setMedicationDetails((prev: any) => ({ ...prev, [field]: value }));
	}, []);

	const setScript = useCallback(
		(patch: Partial<Medication>) => {
			setCurrentMedication((prev) => ({ ...prev, ...patch }));
		},
		[setCurrentMedication],
	);

	// ── Catalog search ────────────────────────────────────────────────────────

	const searchCatalog = useCallback(
		debounce(async (term: string) => {
			if (!term || term.length < 2) {
				setCatalogOptions([]);
				return;
			}
			setCatalogSearching(true);
			try {
				const results = await fetchCatalogMedications({ search: term });
				setCatalogOptions(
					results.map((r) => ({
						value: r.id,
						label: r.generic_name ? `${r.name} (${r.generic_name})` : r.name,
						item: r,
					})),
				);
			} catch {
				/* silent */
			} finally {
				setCatalogSearching(false);
			}
		}, 300),
		[],
	);

	// Prefill the guide-derived fields (dosage/frequency/instructions + route/refills).
	// Shared by the initial catalog pick and the dosage-guide picker below.
	const applyGuide = useCallback(
		(guide?: DosageGuide) => {
			setCurrentMedication((prev) => ({
				...prev,
				dosage: guide?.dosage ?? prev.dosage,
				frequency: guide?.frequency ?? prev.frequency,
				instructions: guide?.instructions ?? prev.instructions,
			}));
			setMedicationDetails((prev: any) => ({
				...prev,
				administration_route: guide?.administration_route ?? prev.administration_route,
				refills_authorized: guide?.refills_authorized ?? prev.refills_authorized,
			}));
		},
		[setCurrentMedication],
	);

	const applyFromCatalog = useCallback(
		(catalogId: number) => {
			const opt = catalogOptions.find((o) => o.value === catalogId);
			if (!opt) return;
			const c = opt.item;
			const guide = defaultDosageGuide(c.dosage_guides);
			setSelectedCatalogItem(c);
			setSelectedGuideLabel(guide?.label);
			setCurrentMedication((prev) => ({
				...prev,
				medication_catalog_id: c.id,
				medication_name: c.name,
			}));
			setMedicationDetails((prev: any) => ({
				...prev,
				medication_category: c.medication_category ?? prev.medication_category,
			}));
			applyGuide(guide);
		},
		[catalogOptions, setCurrentMedication, applyGuide],
	);

	// Re-apply the prefill from a different dosage guide (only offered when 2+ guides exist)
	const applyGuideByLabel = useCallback(
		(label: string) => {
			const guide = selectedCatalogItem?.dosage_guides?.find((g) => g.label === label);
			if (!guide) return;
			setSelectedGuideLabel(label);
			applyGuide(guide);
		},
		[selectedCatalogItem, applyGuide],
	);

	// Clearing the catalog selection hides the guide picker but keeps any values already prefilled
	const handleCatalogSelectChange = useCallback(
		(catalogId?: number) => {
			if (catalogId == null) {
				setSelectedCatalogItem(null);
				setSelectedGuideLabel(undefined);
				return;
			}
			applyFromCatalog(catalogId);
		},
		[applyFromCatalog],
	);

	// ── E-script (immediate, not deferred to Save) ────────────────────────────

	const handleEscriptSubmit = async () => {
		if (!escriptUrl || !currentMedication.id) return;
		setEscriptSubmitting(true);
		try {
			const { data } = await submitEscriptPractitioner(currentMedication.id, escriptUrl);
			setCurrentMedication((prev) => ({
				...prev,
				script_type: data.script_type,
				script_url: data.script_url,
				script_file_id: data.script_file_id,
				script_uploaded_at: data.script_uploaded_at,
				script_uploaded_by: data.script_uploaded_by,
				script_status: data.script_status,
			}));
			setEscriptUrl("");
			showSuccess("E-script submitted successfully");
		} catch {
			showError("Failed to submit e-script. Please try again.");
		} finally {
			setEscriptSubmitting(false);
		}
	};

	// ── Script status options ─────────────────────────────────────────────────

	const scriptStatusOptions = [
		{ value: "pending", label: "Pending — script required from patient" },
		{ value: "uploaded", label: "Uploaded — awaiting verification" },
		{ value: "verified", label: "Verified — script confirmed" },
		{ value: "expired", label: "Expired — new script required" },
	];

	// ── Tab items ─────────────────────────────────────────────────────────────

	const tabItems = [
		// ── Basic Information ─────────────────────────────────────────────────
		{
			key: "basic",
			label: "Basic Information",
			children: (
				<>
					{/* Catalog pre-fill banner */}
					<SectionCard style={{ marginBottom: 16, background: "var(--mg-info-bg)", border: "1px solid var(--mg-primary-border)" }}>
						<Space align="center" style={{ width: "100%", flexWrap: "wrap", gap: 8 }}>
							<BookOutlined style={{ color: "var(--mg-primary)", fontSize: 16 }} />
							<Text style={{ fontSize: 13, color: "var(--mg-primary)", fontWeight: 500 }}>Pre-fill from catalog</Text>
							<Select
								showSearch
								filterOption={false}
								onSearch={searchCatalog}
								onChange={handleCatalogSelectChange}
								loading={catalogSearching}
								placeholder="Search medication catalog…"
								style={{ width: 320 }}
								options={catalogOptions}
								notFoundContent={catalogSearching ? "Searching…" : "Type to search"}
								allowClear
							/>
							{selectedCatalogItem && (selectedCatalogItem.dosage_guides?.length ?? 0) >= 2 && (
								<Select
									value={selectedGuideLabel}
									onChange={applyGuideByLabel}
									placeholder="Select dosage guide"
									style={{ width: 320 }}
									listHeight={320}
									options={selectedCatalogItem.dosage_guides!.map((g) => ({ value: g.label, label: g.label, guide: g }))}
									optionRender={(option) => {
										const g = option.data.guide as DosageGuide;
										return (
											<div>
												<Space size={6}>
													<span style={{ fontSize: 13, fontWeight: 500 }}>{g.label}</span>
													{g.is_default && (
														<ContrastTag background="var(--mg-primary)" style={{ fontSize: 10, lineHeight: "16px", margin: 0 }}>
															Default
														</ContrastTag>
													)}
												</Space>
												{g.description && <div style={{ fontSize: 11, color: "var(--mg-text-secondary)", whiteSpace: "normal" }}>{g.description}</div>}
											</div>
										);
									}}
								/>
							)}
							<Text type="secondary" style={{ fontSize: 11 }}>
								Selecting a catalog item pre-fills all default values below
							</Text>
						</Space>
					</SectionCard>

					<Row gutter={[16, 16]}>
						{/* Left column — classification */}
						<Col xs={24} md={6}>
							<SectionCard>
								<Form layout="vertical">
									<Form.Item
										label={
											<>
												<Text strong>Medication Name</Text> <Text type="danger">*</Text>
											</>
										}
									>
										<Input value={currentMedication?.medication_name || ""} onChange={(e) => setCurrentMedication((p) => ({ ...p, medication_name: e.target.value }))} placeholder="Enter medication name" />
									</Form.Item>
									<Form.Item label={<Text strong>Medication Category</Text>}>
										<Select value={medicationDetails.medication_category || ""} options={MEDICATION_CATEGORIES} onChange={(v) => handleDetailChange("medication_category", v)} placeholder="Select category" showSearch />
									</Form.Item>
									<Form.Item label={<Text strong>Administration Route</Text>}>
										<Select value={medicationDetails.administration_route || ""} options={ADMINISTRATION_ROUTES} onChange={(v) => handleDetailChange("administration_route", v)} placeholder="Select route" showSearch />
									</Form.Item>
										<SectionCard style={{ background: "var(--mg-bg-subtle)", marginTop: 16 }}>
											<Space align="start">
												<InfoCircleOutlined style={{ color: "var(--mg-primary)", fontSize: 16 }} />
											<Text type="secondary" style={{ fontSize: 12, lineHeight: 1.5 }}>
												Fill out basic info first, then navigate to other tabs for additional details.
											</Text>
										</Space>
										</SectionCard>
									</Form>
								</SectionCard>
							</Col>

							{/* Right column — prescription details */}
							<Col xs={24} md={18}>
								<SectionCard>
									<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
										<Title level={5} style={{ margin: 0 }}>
											Medication Details
										</Title>
										{isEditing ? <Badge count="Editing" style={{ backgroundColor: "var(--mg-info)" }} /> : <Badge count="New" style={{ backgroundColor: "var(--mg-success)" }} />}
								</div>
								<Divider style={{ marginTop: 12, marginBottom: 16 }} />
								<Form layout="vertical">
									<Row gutter={[16, 16]}>
										<Col xs={24} md={12}>
											<Form.Item
												label={
													<>
														Dosage <Text type="danger">*</Text>
													</>
												}
											>
												<Input value={currentMedication?.dosage || ""} onChange={(e) => setCurrentMedication((p) => ({ ...p, dosage: e.target.value }))} placeholder="e.g., 10mg, 500mg, 5ml" />
											</Form.Item>
										</Col>
										<Col xs={24} md={12}>
											<Form.Item
												label={
													<>
														Frequency <Text type="danger">*</Text>
													</>
												}
											>
												<Select value={currentMedication?.frequency || ""} options={FREQUENCY_OPTIONS} onChange={(v) => setCurrentMedication((p) => ({ ...p, frequency: v }))} placeholder="Select frequency" />
											</Form.Item>
										</Col>
										<Col xs={24} md={12}>
											<Form.Item
												label={
													<>
														Start Date <Text type="danger">*</Text>
													</>
												}
											>
												<DatePicker
													style={{ width: "100%" }}
													value={currentMedication?.start_date ? dayjs(currentMedication.start_date) : null}
													onChange={(d) => setCurrentMedication((p) => ({ ...p, start_date: d ? d.toDate() : new Date() }))}
													format={dateFormat}
												/>
											</Form.Item>
										</Col>
										<Col xs={24} md={12}>
											<Form.Item label="End Date">
												<DatePicker
													style={{ width: "100%" }}
													value={currentMedication?.end_date ? dayjs(currentMedication.end_date) : null}
													onChange={(d) => setCurrentMedication((p) => ({ ...p, end_date: d ? d.toDate() : undefined }))}
													format={dateFormat}
												/>
											</Form.Item>
										</Col>
										<Col xs={24} md={12}>
											<Form.Item label="Prescribed By">
												<Input value={currentMedication?.prescribed_by || ""} onChange={(e) => setCurrentMedication((p) => ({ ...p, prescribed_by: e.target.value }))} />
											</Form.Item>
										</Col>
										<Col xs={24} md={12}>
											<Form.Item label="Prescribed Date">
												<DatePicker
													style={{ width: "100%" }}
													value={medicationDetails.prescribed_date ? dayjs(medicationDetails.prescribed_date) : null}
													onChange={(d) => handleDetailChange("prescribed_date", d ? d.toDate() : null)}
													format={dateFormat}
												/>
											</Form.Item>
										</Col>
										<Col xs={24}>
											<Form.Item label="Instructions">
												<TextArea
													value={currentMedication?.instructions || ""}
													onChange={(e) => setCurrentMedication((p) => ({ ...p, instructions: e.target.value }))}
													rows={3}
													placeholder="Special instructions for taking this medication"
												/>
											</Form.Item>
										</Col>
										<Col xs={24}>
											<Form.Item label="Reason for Medication">
												<TextArea
													value={medicationDetails.reason_for_medication || ""}
													onChange={(e) => handleDetailChange("reason_for_medication", e.target.value)}
													rows={3}
													placeholder="Clinical indication or reason for prescribing"
												/>
											</Form.Item>
										</Col>
										<Col xs={24} md={12}>
											<Checkbox checked={currentMedication?.active ?? true} onChange={(e) => setCurrentMedication((p) => ({ ...p, active: e.target.checked }))}>
												Active
											</Checkbox>
										</Col>
										<Col xs={24} md={12}>
											<Checkbox checked={medicationDetails.allergies_checked || false} onChange={(e) => handleDetailChange("allergies_checked", e.target.checked)}>
												Allergies Checked
											</Checkbox>
										</Col>
									</Row>
								</Form>
								</SectionCard>
							</Col>
						</Row>
					</>
			),
		},

		// ── Pharmacy & Supply ─────────────────────────────────────────────────
		{
			key: "pharmacy",
			label: "Pharmacy & Supply",
			children: (
				<SectionCard>
					<Form layout="vertical">
						<Row gutter={[16, 16]}>
							<Col xs={24} md={12}>
								<Title level={5}>Pharmacy Information</Title>
								<Form.Item label="Pharmacy Name">
									<Input value={medicationDetails.pharmacy_name || ""} onChange={(e) => handleDetailChange("pharmacy_name", e.target.value)} />
								</Form.Item>
								<Form.Item label="Pharmacy Phone">
									<Input value={medicationDetails.pharmacy_phone || ""} onChange={(e) => handleDetailChange("pharmacy_phone", e.target.value)} />
								</Form.Item>
								<Form.Item label="Prescription Expiration">
									<DatePicker
										style={{ width: "100%" }}
										value={medicationDetails.prescription_expiration ? dayjs(medicationDetails.prescription_expiration) : null}
										onChange={(d) => handleDetailChange("prescription_expiration", d ? d.toDate() : null)}
										format={dateFormat}
									/>
								</Form.Item>
							</Col>
							<Col xs={24} md={12}>
								<Title level={5}>Refill Information</Title>
								<Form.Item label="Refills Authorized">
									<InputNumber style={{ width: "100%" }} value={medicationDetails.refills_authorized || 0} onChange={(v) => handleDetailChange("refills_authorized", v)} min={0} />
								</Form.Item>
								<Form.Item label="Refills Remaining">
									<InputNumber style={{ width: "100%" }} value={medicationDetails.refills_remaining || 0} onChange={(v) => handleDetailChange("refills_remaining", v)} min={0} />
								</Form.Item>
								<Form.Item label="Last Refill Date">
									<DatePicker
										style={{ width: "100%" }}
										value={medicationDetails.last_refill_date ? dayjs(medicationDetails.last_refill_date) : null}
										onChange={(d) => handleDetailChange("last_refill_date", d ? d.toDate() : null)}
										format={dateFormat}
									/>
								</Form.Item>
							</Col>
						</Row>
					</Form>
				</SectionCard>
			),
		},

		// ── Patient Response ──────────────────────────────────────────────────
		{
			key: "response",
			label: "Patient Response",
			children: (
				<SectionCard>
					<Form layout="vertical">
						<Row gutter={[16, 16]}>
							<Col xs={24} md={12}>
								<Title level={5}>Effectiveness</Title>
								<Form.Item label="Patient Response">
									<TextArea
										value={medicationDetails.patient_response || ""}
										onChange={(e) => handleDetailChange("patient_response", e.target.value)}
										rows={4}
										placeholder="Document how the patient is responding to this medication"
									/>
								</Form.Item>
								<Form.Item label="Medication Adherence (%)">
									<InputNumber style={{ width: "100%" }} value={medicationDetails.medication_adherence || null} onChange={(v) => handleDetailChange("medication_adherence", v)} min={0} max={100} suffix="%" />
								</Form.Item>
							</Col>
							<Col xs={24} md={12}>
								<Title level={5}>Side Effects</Title>
								<Form.Item label="Side Effects Observed">
									<TextArea
										value={medicationDetails.side_effects_observed || ""}
										onChange={(e) => handleDetailChange("side_effects_observed", e.target.value)}
										rows={4}
										placeholder="Document any side effects the patient has experienced"
									/>
								</Form.Item>
								<Space direction="vertical" style={{ marginTop: 16 }}>
									<Checkbox checked={medicationDetails.side_effect_reported || false} onChange={(e) => handleDetailChange("side_effect_reported", e.target.checked)}>
										Side effects reported to provider
									</Checkbox>
									<Checkbox checked={medicationDetails.followup_scheduled || false} onChange={(e) => handleDetailChange("followup_scheduled", e.target.checked)}>
										Follow-up appointment scheduled
									</Checkbox>
								</Space>
							</Col>
						</Row>
					</Form>
				</SectionCard>
			),
		},

		// ── Script / Prescription ─────────────────────────────────────────────
		{
			key: "script",
			label: <ScriptTabLabel status={currentMedication.script_status} />,
			children: (
				<Row gutter={[16, 16]}>
					{/* Left — status & metadata */}
					<Col xs={24} md={10}>
						<SectionCard style={{ height: "100%" }}>
							<Title level={5} style={{ marginBottom: 16 }}>
								Script Status
							</Title>

							{/* Current status summary */}
							{currentMedication.script_status ? (
								<div
									style={{
										padding: "12px 16px",
										borderRadius: 8,
										marginBottom: 16,
											border: `1px solid ${
												currentMedication.script_status === "verified" ? "var(--mg-success)" : currentMedication.script_status === "uploaded" ? "var(--mg-info)" : currentMedication.script_status === "expired" ? "var(--mg-danger)" : "var(--mg-warning)"
											}`,
											background: currentMedication.script_status === "verified" ? "var(--mg-success-bg)" : currentMedication.script_status === "uploaded" ? "var(--mg-info-bg)" : currentMedication.script_status === "expired" ? "var(--mg-danger-bg)" : "var(--mg-warning-bg)",
									}}
								>
									<Space>
										<ScriptStatusIcon status={currentMedication.script_status} />
										<Text strong style={{ color: SCRIPT_STATUS_CONFIG[currentMedication.script_status].color }}>
											{SCRIPT_STATUS_CONFIG[currentMedication.script_status].label}
										</Text>
									</Space>
									{(currentMedication.script_uploaded_at || currentMedication.script_file_id || currentMedication.script_url) && (
										<Descriptions size="small" column={1} style={{ marginTop: 10 }}>
											{currentMedication.script_uploaded_at && (
												<Descriptions.Item label="Submitted">
													<Text style={{ fontSize: 12 }}>{dayjs(currentMedication.script_uploaded_at).format("D MMM YYYY HH:mm")}</Text>
												</Descriptions.Item>
											)}
											{currentMedication.script_type === "escript" && currentMedication.script_url && (
												<Descriptions.Item label="E-Script">
													<a href={currentMedication.script_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11 }}>
														{currentMedication.script_url}
													</a>
												</Descriptions.Item>
											)}
											{currentMedication.script_type === "file" && currentMedication.script_file_id && (
												<Descriptions.Item label="File ref">
													<Text style={{ fontSize: 11, fontFamily: "monospace" }} ellipsis={{ tooltip: currentMedication.script_file_id }}>
														{currentMedication.script_file_id.startsWith("local:") ? "Stored locally" : currentMedication.script_file_id}
													</Text>
												</Descriptions.Item>
											)}
										</Descriptions>
									)}
								</div>
							) : (
								<Alert message="No script status set" description="Set the script status below to begin tracking prescription compliance." type="info" showIcon style={{ marginBottom: 16 }} />
							)}

							{/* Status selector — saved with main Save button */}
							<Form layout="vertical">
								<Form.Item label={<Text strong>Set Status</Text>} extra="Changes are applied when you click Save.">
									<Select
										value={currentMedication.script_status ?? undefined}
										onChange={(v) => setScript({ script_status: v })}
										options={scriptStatusOptions}
										placeholder="Select script status"
										style={{ width: "100%" }}
										allowClear
									/>
								</Form.Item>

								<Form.Item label={<Text strong>Script Notes</Text>}>
									<TextArea
										value={currentMedication.script_notes || ""}
										onChange={(e) => setScript({ script_notes: e.target.value })}
										rows={4}
										placeholder="e.g. repeat script, original issued by GP, expires 12 Jan 2026"
										maxLength={1000}
										showCount
									/>
								</Form.Item>
							</Form>
						</SectionCard>
					</Col>

					{/* Right — e-script */}
					<Col xs={24} md={14}>
						<SectionCard style={{ height: "100%" }}>
							<Title level={5} style={{ marginBottom: 4 }}>
								Attach Script
							</Title>
							<Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 12 }}>
								Submit an e-script link. Changes apply immediately — no need to click Save.
							</Text>

							{/* Existing e-script notice */}
							{currentMedication.script_url && currentMedication.script_type === "escript" && (
								<Alert
									message={
										<span>
											E-script on file:{" "}
											<a href={currentMedication.script_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12 }}>
												{currentMedication.script_url}
											</a>
										</span>
									}
									description="Submitting a new link will replace the existing one."
									type="warning"
									showIcon
									style={{ marginBottom: 12 }}
								/>
							)}

							<div style={{ marginBottom: 12 }}>
								<Text style={{ fontSize: 12, display: "block", marginBottom: 6 }}>
									E-Script URL <Text type="danger">*</Text>
								</Text>
								<Input
									prefix={<LinkOutlined style={{ color: "#1677ff" }} />}
									value={escriptUrl}
									onChange={(e) => setEscriptUrl(e.target.value)}
									placeholder="https://escripts.example.com/rx/abc123"
									disabled={escriptSubmitting || !isEditing}
								/>
							</div>

							{escriptSubmitting && <Progress percent={100} size="small" status="active" style={{ marginBottom: 12 }} />}

							<Button
								type="primary"
								block
								onClick={handleEscriptSubmit}
								loading={escriptSubmitting}
								disabled={escriptSubmitting || !escriptUrl || !isEditing}
							>
								{escriptSubmitting ? "Submitting…" : "Submit E-Script"}
							</Button>

							{/* Help text for new medications */}
							{!isEditing && <Alert message="Save the medication record first" description="Script attachment is available after the medication has been created." type="info" showIcon style={{ marginTop: 12 }} />}
						</SectionCard>
					</Col>
				</Row>
			),
		},
	];

	// ── Render ────────────────────────────────────────────────────────────────

	if (isConsult) {
		return <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} />;
	}

	return (
		<Modal
			title={
				<Space>
					<Title level={4} style={{ margin: 0 }}>
						{isEditing ? "Edit Medication" : "Add Medication"}
					</Title>
					{isEditing && <Badge count="Editing" style={{ backgroundColor: "var(--mg-info)" }} />}
				</Space>
			}
			open={isVisible}
			onCancel={onClose}
			width="90vw"
			style={{ maxWidth: 1200 }}
			destroyOnClose
			footer={[
				<Button key="cancel" onClick={onClose} disabled={loading}>Cancel</Button>,
				<Button key="submit" type="primary" onClick={saveMedication} loading={loading}>
					{isEditing ? "Update" : "Save"}
				</Button>,
			]}
		>
			<Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} />
		</Modal>
	);
};

export default CreateMedicationModal;
